import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import TaskCard from "@/components/TaskCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass, Funnel } from "@phosphor-icons/react";

export default function Marketplace() {
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const PAGE = 24;
  const [filters, setFilters] = useState({
    department_id: "all", priority: "all", sort: "newest", skill: "",
  });

  const load = async (reset = true) => {
    const params = { sort: filters.sort, paginated: 1, limit: PAGE, skip: reset ? 0 : skip };
    if (q) params.q = q;
    if (filters.department_id !== "all") params.department_id = filters.department_id;
    if (filters.priority !== "all") params.priority = filters.priority;
    if (filters.skill) params.skill = filters.skill;
    const { data } = await api.get("/tasks", { params });
    if (reset) {
      setTasks(data.items);
      setSkip(data.items.length);
    } else {
      setTasks((prev) => [...prev, ...data.items]);
      setSkip((s) => s + data.items.length);
    }
    setTotal(data.total);
  };

  useEffect(() => { api.get("/departments").then(({ data }) => setDepts(data)); }, []);
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [filters, q]);

  const deptMap = useMemo(() => Object.fromEntries(depts.map((d) => [d.dept_id, d.name])), [depts]);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="marketplace-page">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Marketplace</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold mt-1">Discover work</h1>
          <p className="text-sm text-zinc-500 mt-1">Browse open tasks across the organization.</p>
        </div>
      </div>

      {/* Filters bar */}
      <div className="border border-border p-3 md:p-4 bg-background mb-6 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4 flex items-center gap-2 border border-border px-3 rounded-sm">
          <MagnifyingGlass size={14} className="text-zinc-400" />
          <input
            data-testid="marketplace-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title or description…"
            className="bg-transparent w-full py-2 text-sm outline-none"
          />
        </div>
        <Select value={filters.department_id} onValueChange={(v) => setFilters((f) => ({ ...f, department_id: v }))}>
          <SelectTrigger className="md:col-span-2 rounded-sm" data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {depts.map((d) => <SelectItem key={d.dept_id} value={d.dept_id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters((f) => ({ ...f, priority: v }))}>
          <SelectTrigger className="md:col-span-2 rounded-sm" data-testid="filter-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid="filter-skill"
          value={filters.skill}
          onChange={(e) => setFilters((f) => ({ ...f, skill: e.target.value }))}
          placeholder="Skill (e.g. python)"
          className="md:col-span-2 rounded-sm"
        />
        <Select value={filters.sort} onValueChange={(v) => setFilters((f) => ({ ...f, sort: v }))}>
          <SelectTrigger className="md:col-span-2 rounded-sm" data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="highest_reward">Highest reward</SelectItem>
            <SelectItem value="highest_credits">Highest credits</SelectItem>
            <SelectItem value="urgent">Urgent (deadline)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs text-zinc-500 mb-3 uppercase tracking-wider" data-testid="marketplace-count">
        Showing {tasks.length} of {total} task{total !== 1 ? "s" : ""}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {tasks.map((t) => (
          <TaskCard key={t.task_id} task={t} departmentName={deptMap[t.department_id]} />
        ))}
        {tasks.length === 0 && (
          <div className="col-span-full text-center text-sm text-zinc-500 p-12 border border-dashed border-border" data-testid="marketplace-empty">
            No tasks match your filters. Try clearing them.
          </div>
        )}
      </div>
      {tasks.length < total && (
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={() => load(false)} className="rounded-sm" data-testid="marketplace-load-more">
            Load more ({total - tasks.length} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
